import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex items-center justify-center px-4 py-3 bg-white border-t border-gray-200" }
const _hoisted_2 = { class: "flex flex-1 justify-between sm:hidden" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "hidden sm:flex sm:items-center sm:space-x-4" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "flex items-center space-x-1" }
const _hoisted_8 = {
  key: 1,
  class: "relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 2,
  class: "relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded"
}
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "ml-4 text-sm text-gray-700" }

import { computed } from "vue";

interface Props {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  maxVisiblePages?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Pagination',
  props: {
    currentPage: {},
    totalItems: {},
    itemsPerPage: {},
    maxVisiblePages: { default: 7 }
  },
  emits: ["page-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Computed properties
const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.itemsPerPage;
  return Math.min(end, props.totalItems);
});

const visiblePages = computed(() => {
  const pages: number[] = [];
  const { currentPage, maxVisiblePages } = props;
  const total = totalPages.value;

  if (total <= maxVisiblePages) {
    // Show all pages if total is less than max visible
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // Calculate the range of pages to show
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let end = Math.min(total, start + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }

  return pages;
});

const showFirstPage = computed(() => {
  return totalPages.value > props.maxVisiblePages && visiblePages.value[0] > 1;
});

const showLastPage = computed(() => {
  return (
    totalPages.value > props.maxVisiblePages &&
    visiblePages.value[visiblePages.value.length - 1] < totalPages.value
  );
});

const showFirstEllipsis = computed(() => {
  return showFirstPage.value && visiblePages.value[0] > 2;
});

const showLastEllipsis = computed(() => {
  return (
    showLastPage.value &&
    visiblePages.value[visiblePages.value.length - 1] < totalPages.value - 1
  );
});

// Methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit("page-change", page);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (goToPage(_ctx.currentPage - 1))),
        disabled: _ctx.currentPage === 1,
        class: "relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      }, " Previous ", 8, _hoisted_3),
      _createElementVNode("button", {
        onClick: _cache[1] || (_cache[1] = ($event: any) => (goToPage(_ctx.currentPage + 1))),
        disabled: _ctx.currentPage === totalPages.value,
        class: "relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      }, " Next ", 8, _hoisted_4)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("button", {
        onClick: _cache[2] || (_cache[2] = ($event: any) => (goToPage(_ctx.currentPage - 1))),
        disabled: _ctx.currentPage === 1,
        class: "relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      }, [...(_cache[6] || (_cache[6] = [
        _createElementVNode("span", { class: "sr-only" }, "Previous", -1),
        _createElementVNode("svg", {
          class: "w-4 h-4",
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 20 20",
          fill: "currentColor",
          "aria-hidden": "true"
        }, [
          _createElementVNode("path", {
            "fill-rule": "evenodd",
            d: "M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z",
            "clip-rule": "evenodd"
          })
        ], -1)
      ]))], 8, _hoisted_6),
      _createElementVNode("div", _hoisted_7, [
        (showFirstPage.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[3] || (_cache[3] = ($event: any) => (goToPage(1))),
              class: "relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            }, " 1 "))
          : _createCommentVNode("", true),
        (showFirstEllipsis.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_8, " ... "))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (page) => {
          return (_openBlock(), _createElementBlock("button", {
            key: page,
            onClick: ($event: any) => (goToPage(page)),
            class: _normalizeClass([
            'relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium border rounded',
            page === _ctx.currentPage
              ? 'z-10 bg-[#157813] border-[#157813] text-white'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
          ])
          }, _toDisplayString(page), 11, _hoisted_9))
        }), 128)),
        (showLastEllipsis.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_10, " ... "))
          : _createCommentVNode("", true),
        (showLastPage.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 3,
              onClick: _cache[4] || (_cache[4] = ($event: any) => (goToPage(totalPages.value))),
              class: "relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            }, _toDisplayString(totalPages.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("button", {
        onClick: _cache[5] || (_cache[5] = ($event: any) => (goToPage(_ctx.currentPage + 1))),
        disabled: _ctx.currentPage === totalPages.value,
        class: "relative inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      }, [...(_cache[7] || (_cache[7] = [
        _createElementVNode("span", { class: "sr-only" }, "Next", -1),
        _createElementVNode("svg", {
          class: "w-4 h-4",
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 20 20",
          fill: "currentColor",
          "aria-hidden": "true"
        }, [
          _createElementVNode("path", {
            "fill-rule": "evenodd",
            d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z",
            "clip-rule": "evenodd"
          })
        ], -1)
      ]))], 8, _hoisted_11),
      _createElementVNode("div", _hoisted_12, " Page " + _toDisplayString(_ctx.currentPage) + " of " + _toDisplayString(totalPages.value), 1)
    ])
  ]))
}
}

})